import { useQuery } from 'react-query';
import { useMemo } from 'react';
import {
    BackgroundCheckPackageData,
    ConfigResponse,
    FetchedBackgroundCheckPackageData,
    FetchedOrganizationPackagesData,
} from '../types/complianceTypes';
import { getOrganizationPackagesData } from './index';

export const ORGANIZATION_PACKAGES_QUERY_KEY = 'ORGANIZATION_PACKAGES_QUERY';

const useBackgroundCheckPackageData = (
    isUs: boolean,
    fetchedBackgroundCheckPackageData: FetchedBackgroundCheckPackageData | undefined
) =>
    useMemo(() => {
        if (!fetchedBackgroundCheckPackageData) {
            return undefined;
        }
        const {
            monthlyCredit,
            discountInPercents,
            usPrice,
            nonUsPrice,
            usTypeId,
            nonUsTypeId,
            currency,
            timeEstimation,
            packageItemsIds,
        } = fetchedBackgroundCheckPackageData || {};

        const servicePrice = isUs ? usPrice : nonUsPrice;
        const priceToPay = servicePrice - (servicePrice * discountInPercents) / 100;

        return {
            typeId: isUs ? usTypeId : nonUsTypeId,
            timeEstimation,
            price: servicePrice,
            discountInPercents,
            priceToPay,
            currency,
            monthlyCredit: discountInPercents ? monthlyCredit : undefined,
            packageItemsIds: discountInPercents ? undefined : packageItemsIds,
        };
    }, [isUs, fetchedBackgroundCheckPackageData]);

export const useOrganizationPackagesData = (isUs: boolean, autofetch = true) => {
    const initialData: ConfigResponse | undefined = undefined; // TODO: complete initial data from batch GET request

    const userOrganizationPackageDataQuery = useQuery({
        queryKey: [ORGANIZATION_PACKAGES_QUERY_KEY],
        queryFn: async (): Promise<FetchedOrganizationPackagesData> => getOrganizationPackagesData(),
        enabled: autofetch,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        staleTime: Infinity,
        initialData,
    });

    const fetchedData = userOrganizationPackageDataQuery?.data;
    const backgroundCheckPackageData = useBackgroundCheckPackageData(isUs, fetchedData?.backgroundCheckPackageData);

    return {
        isPackagesDataLoading: userOrganizationPackageDataQuery?.isLoading,
        backgroundCheckPackageData: backgroundCheckPackageData as BackgroundCheckPackageData,
        legalDocumentsPackageData: fetchedData?.legalDocsPackageData,
        workforceClassificationPackageData: fetchedData?.workforceClassificationPackageData,
        refetchPackagesData: () => userOrganizationPackageDataQuery.refetch({ stale: true }),
    };
};
